import { Anchor, Box, Group, Paper, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconUsers } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import type { FeePolicy } from "../../apis/tenantFeePolicy";
import { useEditPolicy } from "../../hooks/useEditPolicy";
import { formatMoney, formatPercent } from "../../utils/format";
import { formatTierRange, formatTierRate, headlineLabel, readTiers } from "../../utils/fee-policy-labels";
import { FeePolicyStatusBadge } from "./FeePolicyStatusBadge";

interface Props {
  policy: FeePolicy;
  customerCount?: number;
  /** True once the customer-reach counts have loaded — lets us show a real 0-state
   *  ("Хараахан оноогоогүй") instead of a bare dash while still loading. */
  countsLoaded?: boolean;
}

/** Spec §2.3 — NAMED grid card. Whole card opens the read-only view; the
 *  "Засах →" link opens edit mode. The headline is method-aware: a single figure
 *  for percent/flat, or the full tier ladder (price range → rate) for tiered. */
export function PolicyMiniCard({ policy, customerCount, countsLoaded }: Props) {
  const navigate = useNavigate();
  const editPolicy = useEditPolicy();

  const method = policy.calculationMethod;
  const tiers = method === "TIERED" ? readTiers(policy) : [];

  const count = customerCount ?? 0;
  const reach = !countsLoaded ? "—" : count > 0 ? `${count} харилцагч` : "Хараахан оноогоогүй";

  return (
    <Paper
      p="md"
      withBorder
      radius="md"
      style={{ cursor: "pointer", height: "100%" }}
      onClick={() => navigate(`/my-billing/fee-policy/${policy.id}`)}>
      <Stack gap="xs" h="100%">
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Text fw={600} lineClamp={1}>{policy.name ?? "(нэргүй)"}</Text>
          <FeePolicyStatusBadge status={policy.status} />
        </Group>

        <Box>
          <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>
            {headlineLabel(method)}
          </Text>

          {method === "PERCENTAGE" && (
            <Title order={3} style={{ fontVariantNumeric: "tabular-nums" }}>
              {policy.feeRate ? formatPercent(Number(policy.feeRate) * 100) : "—"}
            </Title>
          )}

          {method === "FLAT" && (
            <Title order={3} style={{ fontVariantNumeric: "tabular-nums" }}>
              {policy.flatFeeAmount ? formatMoney(policy.flatFeeAmount, policy.feeCurrency) : "—"}
            </Title>
          )}

          {method === "TIERED" && (
            tiers.length > 0 ? (
              <Stack gap={4} mt={4}>
                {tiers.map((t, i) => (
                  <Group key={i} justify="space-between" wrap="nowrap" gap="xs">
                    <Text size="xs" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatTierRange(t, policy.feeCurrency)}
                    </Text>
                    <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatTierRate(t)}
                    </Text>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Title order={3}>Шатлалаар</Title>
            )
          )}
        </Box>

        <Group justify="space-between" mt="auto" pt="xs" wrap="nowrap" gap="xs">
          <Group gap={6} wrap="nowrap">
            <ThemeIcon variant="light" color="gray" size="sm" radius="sm"><IconUsers size={14} /></ThemeIcon>
            <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>{reach}</Text>
          </Group>
          <Anchor
            size="sm"
            fw={500}
            onClick={(e) => { e.stopPropagation(); void editPolicy(policy); }}>
            Засах →
          </Anchor>
        </Group>
      </Stack>
    </Paper>
  );
}
